// src/viewmodels/useHomeViewModel.ts
//
// 홈 화면의 대시보드 데이터를 캡슐화
// ServiceRepository.fetchDashboard → useServiceStore 구독

import { useEffect, useMemo, useState } from 'react';
import { ServiceRepository } from '../repositories/service.repository.ts';
import { useServiceStore } from '../stores/service.store.ts';
import type { MainBanner, Category, RelatedServiceGroup, SubBanner, UmmahPosts, HomeNotice } from '../models/service_home.model.ts';
import { useEnsureDashboard } from '../hooks/useEnsureDashboard.ts';

export interface HomeViewModel {
  isLoading: boolean;
  error: string | null;
  mainBanners: MainBanner[];
  categories: Category[];
  activeRelatedGroups: RelatedServiceGroup[];
  activeSubBanners: SubBanner[];
  ummahPosts: UmmahPosts | null;
  hasCommunity: boolean;
  homeNotice: HomeNotice | null;
  q: string;
  onChangeQ: (v: string) => void;
  recommendedKeywords: string[];
  searchHistories: string[];
}

export function useHomeViewModel(): HomeViewModel {
  const isLoading = useServiceStore((s) => s.dashboardLoading);
  const error = useServiceStore((s) => s.dashboardError);
  const mainBanners = useServiceStore((s) => s.main_banners);
  const categories = useServiceStore((s) => s.categories);
  const relatedServices = useServiceStore((s) => s.related_services);
  const subBanners = useServiceStore((s) => s.sub_banners);
  const ummahPosts = useServiceStore((s) => s.ummah_posts);
  const homeNotice = useServiceStore((s) => s.home_notice);
  const recommendedKeywords = useServiceStore((s) => s.recommended_keywords);
  const searchHistories = useServiceStore((s) => s.search_histories);

  const [q, setQ] = useState('');

  useEnsureDashboard();

  useEffect(() => {
    ServiceRepository.fetchSearchHome();
  }, []);

  const activeRelatedGroups = useMemo(() => {
    const groups = Array.isArray(relatedServices) ? relatedServices : [];
    return groups
      .filter((g) => g?.status === true)
      .map((g) => ({
        ...g,
        services: (Array.isArray(g?.services) ? g.services : []).filter((s) => s?.status === true),
      }))
      .filter((g) => g.services.length > 0);
  }, [relatedServices]);

  const hasCommunity = useMemo(() => {
    const posts = ummahPosts?.posts ?? [];
    return Array.isArray(posts) && posts.length > 0;
  }, [ummahPosts]);

  const activeSubBanners = useMemo(
    () => (Array.isArray(subBanners) ? subBanners : []).filter((b) => b?.status === true),
    [subBanners]
  );

  return {
    isLoading,
    error,
    mainBanners: mainBanners ?? [],
    categories: categories ?? [],
    activeRelatedGroups,
    activeSubBanners,
    ummahPosts,
    hasCommunity,
    homeNotice: homeNotice ?? null,
    q,
    onChangeQ: setQ,
    recommendedKeywords: recommendedKeywords ?? [],
    searchHistories: searchHistories ?? [],
  };
}
